import { Node, Mark } from "@tiptap/core";
import getXssWhiteList from "./getXssWhiteList";

const SKIP_BLOCK_TAGS = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "ul",
  "ol",
  "li",
  "blockquote",
  "img",
  "video",
  "audio",
  "pre",
]);

const SKIP_INLINE_TAGS = new Set([
  "code",
  "strong",
  "b",
  "em",
  "u",
  "s",
  "strike",
  "a",
  "br",
  "hr",
  "i",
  "area",
  "span",
]);

const SKIP_TAGS = new Set([...SKIP_BLOCK_TAGS, ...SKIP_INLINE_TAGS]);

const INLINE_ELEMENTS = new Set([
  "mark",
  "small",
  "del",
  "ins",
  "sub",
  "sup",
  "cite",
  "kbd",
  "abbr",
  "bdi",
  "bdo",
  "big",
  "tt",
]);

const xssWhitelist = getXssWhiteList();
const allTags = Object.keys(xssWhitelist);
const blockTags = allTags.filter(
  (tag) => !INLINE_ELEMENTS.has(tag) && !SKIP_TAGS.has(tag),
);
const inlineTags = allTags.filter(
  (tag) => INLINE_ELEMENTS.has(tag) && !SKIP_TAGS.has(tag),
);
const blockParseTags = blockTags.map((tag) => ({ tag }));
const inlineParseTags = inlineTags.map((tag) => ({ tag }));

export const allBlockTags = [...blockTags, ...Array.from(SKIP_BLOCK_TAGS)];

// ✅ Společná funkce pro atributy
function setAddAttributeSet(tagsToSet: string[]) {
  const allAttrs = new Set<string>();
  tagsToSet.forEach((tag) => {
    const attrs: string[] =
      xssWhitelist[tag as keyof typeof xssWhitelist] || [];
    attrs.forEach((attr) => allAttrs.add(attr));
  });

  const attrs: Record<string, any> = {
    tag: {
      default: null,
      parseHTML: (element: HTMLElement) => element.tagName.toLowerCase(),
    },
  };

  allAttrs.forEach((attr) => {
    attrs[attr] = {
      default: null,
      parseHTML: (element: HTMLElement) => element.getAttribute(attr),
      renderHTML: (attributes: any) => {
        if (attributes[attr] === "") return { [attr]: true };
        if (!attributes[attr]) return {};
        return { [attr]: attributes[attr] };
      },
    };
  });

  return attrs;
}

// ✅ Společná funkce pro detekci block children
function hasBlockChildren(node: string | HTMLElement): boolean {
  if (typeof node === "string") return false;
  return Array.from(node.children).some((child) =>
    allBlockTags.includes(child.tagName.toLowerCase()),
  );
}

// Block elementy s block contentem
export const GenericBlockElementBlock = Node.create({
  name: "genericBlockBlock",
  group: "block",
  content: "block*",

  parseHTML() {
    return blockParseTags.map((tag) => ({
      ...tag,
      getAttrs: (node: string | HTMLElement) =>
        hasBlockChildren(node) ? {} : false,
    }));
  },

  renderHTML({ HTMLAttributes }) {
    const { tag, ...attrs } = HTMLAttributes;
    return [tag || "div", attrs, 0];
  },

  addAttributes() {
    return setAddAttributeSet(blockTags);
  },
});

// Block elementy s inline contentem
export const GenericBlockElementInline = Node.create({
  name: "genericBlockInline",
  group: "block",
  content: "inline*",

  parseHTML() {
    return blockParseTags.map((tag) => ({
      ...tag,
      getAttrs: (node: string | HTMLElement) =>
        !hasBlockChildren(node) ? {} : false,
    }));
  },

  renderHTML({ HTMLAttributes }) {
    const { tag, ...attrs } = HTMLAttributes;
    return [tag || "p", attrs, 0];
  },

  addAttributes() {
    return setAddAttributeSet(blockTags);
  },
});

// Inline marks
export const GenericInlineElement = Mark.create({
  name: "genericInline",

  parseHTML() {
    return inlineParseTags;
  },

  renderHTML({ HTMLAttributes }) {
    const { tag, ...attrs } = HTMLAttributes;
    return [tag || "span", attrs, 0];
  },

  addAttributes() {
    return setAddAttributeSet(inlineTags);
  },
});

function createMediaNode(name: string, tag: string) {
  return Node.create({
    name,
    group: "block",
    atom: true,
    parseHTML() {
      return [{ tag }];
    },
    renderHTML({ HTMLAttributes }) {
      const { tag, ...attrs } = HTMLAttributes;
      return [tag, attrs];
    },
    addAttributes() {
      return setAddAttributeSet([tag]);
    },
  });
}

export const Image = createMediaNode("image", "img");
export const Video = createMediaNode("video", "video");
export const Audio = createMediaNode("audio", "audio");
export const Area = Node.create({
  name: "area",
  group: "inline",
  inline: true,
  atom: true,

  parseHTML() {
    return [{ tag: "area" }];
  },

  renderHTML({ HTMLAttributes }) {
    const { tag, ...attrs } = HTMLAttributes;
    return ["area", attrs];
  },

  addAttributes() {
    return setAddAttributeSet(["area"]);
  },
});
